import { ChildHealthLinks } from '../components/ChildHealthLinks';
import { AncClinicalSummary } from '../components/AncClinicalSummary';
// Patient detail — encounter history at this clinic. Last visit shown as a
// highlighted card, older visits as a compact list. Each row surfaces the
// attending clinician (name + role) so admins can trace who saw whom.

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, clinicApi, patientsApi, type PatientHistoryEncounter } from '../lib/api';
import { PatientProfileSummary } from '../components/PatientProfileSummary';
import { SharedInstructionsPanel } from '../components/SharedInstructionsPanel';

const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
    general: 'General consultation',
    follow_up: 'Follow-up visit',
    antenatal_visit: 'Antenatal visit',
    postnatal_visit: 'Postnatal visit',
    vaccination: 'Vaccination',
    screening: 'Screening',
    emergency: 'Emergency',
};

const CLINICIAN_ROLE_LABELS: Record<string, string> = {
    doctor: 'Doctor',
    nurse: 'Nurse',
    pharmacist: 'Pharmacist',
    chw: 'CHW',
    other: 'Clinician',
};

export function PatientRecordScreen() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const membership = useQuery({ queryKey: ['my-clinic'], queryFn: () => clinicApi.myClinic() });
    const role = membership.data?.member?.role;
    const canOpenVisit = ['admin','doctor','nurse','provider'].includes(role ?? '');
    const { data, isLoading, error } = useQuery({
        queryKey: ['patient-history', id],
        queryFn: () => patientsApi.encounterHistory(id!),
        enabled: !!id,
        retry: false,
    });

    if (isLoading) {
        return <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>;
    }
    if (error) {
        const notFound = error instanceof ApiError && (error.status === 404 || error.code === 'not_found');
        return (
            <div className="space-y-4">
                <Link to="/patient-records" className="pm-btn-ghost inline-block">
                    ← Back to patient records
                </Link>
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    {notFound
                        ? 'No record found for this patient at your clinic.'
                        : 'Couldn’t load this patient’s history.'}
                </div>
            </div>
        );
    }
    if (!data) return null;

    const { patient, encounters } = data;
    const [lastVisit, ...priorVisits] = encounters;
    const displayName = patient.full_name?.trim() || 'Unnamed patient';

    return (
        <div className="space-y-6">
            <button
                type="button"
                onClick={() => navigate('/patient-records')}
                className="pm-btn-ghost inline-flex items-center"
            >
                ← Back to patient records
            </button>

            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-slate-900 text-2xl font-semibold">{displayName}</h1>
                    <span
                        className={
                            patient.on_pamosi
                                ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                                : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
                        }
                    >
                        {patient.on_pamosi ? 'On Pamosi' : 'Not on Pamosi'}
                    </span>
                </div>
                <p className="text-slate-500 text-sm mt-1">{patient.phone_e164}</p>
            </div>

            <PatientProfileSummary patientId={patient.id} canEdit={role === 'admin' || role === 'receptionist'} />

            {/* Phase 2b — shared instructions pinned above the visit log
                so staff see standing notes before scrolling into history. */}
            <ChildHealthLinks patientId={patient.id} canRecordDelivery={canOpenVisit} />
            <AncClinicalSummary patientId={patient.id} />
            <SharedInstructionsPanel patientId={patient.id} />

            {!lastVisit && <section className="pm-card p-5"><h2 className="font-semibold text-slate-900">Visit history</h2><p className="text-slate-500 text-sm mt-1">No visits yet. This saved clinic card is available while registration is being completed.</p></section>}
            {lastVisit && (
                <section className="space-y-2">
                    <h2 className="text-slate-500 text-xs uppercase tracking-wide font-semibold">
                        Last visit
                    </h2>
                    <div className="pm-card p-5">
                        <EncounterCard encounter={lastVisit} canOpen={canOpenVisit} featured />
                    </div>
                </section>
            )}

            {priorVisits.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-slate-500 text-xs uppercase tracking-wide font-semibold">
                        Earlier visits
                    </h2>
                    <div className="pm-card overflow-hidden">
                        <ul className="divide-y divide-slate-100">
                            {priorVisits.map((enc) => (
                                <li key={enc.id} className="px-5 py-3">
                                    <EncounterCard encounter={enc} canOpen={canOpenVisit} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </div>
    );
}

function EncounterCard(
    { encounter, canOpen, featured = false }:
    { encounter: PatientHistoryEncounter; canOpen: boolean; featured?: boolean },
) {
    const typeLabel = ENCOUNTER_TYPE_LABELS[encounter.encounter_type] ?? encounter.encounter_type;
    const clinicianName = encounter.clinician?.full_name?.trim() || 'Attending clinician';
    const clinicianRole = encounter.clinician?.role
        ? CLINICIAN_ROLE_LABELS[encounter.clinician.role] ?? encounter.clinician.role
        : null;

    const content = (
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
                <div className={featured ? 'text-slate-900 font-semibold text-base' : 'text-slate-900 font-medium'}>
                    {typeLabel}
                </div>
                {encounter.chief_complaint && (
                    <div className="text-slate-600 text-sm mt-1">
                        {encounter.chief_complaint}
                    </div>
                )}
                <div className="text-slate-500 text-xs mt-1">
                    Seen by {clinicianName}
                    {clinicianRole ? ` · ${clinicianRole}` : ''}
                </div>
            </div>
            <div className="text-right shrink-0">
                <StatusPill status={encounter.status} />
                <div className="text-slate-400 text-xs mt-1">
                    {prettyDate(encounter.started_at)}
                </div>
            </div>
        </div>
    );
    return canOpen ? <Link to={`/encounters/${encounter.id}`} className="block rounded-lg hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700" aria-label={`Open ${typeLabel} visit from ${prettyDate(encounter.started_at)}`}>{content}</Link> : <details><summary className="cursor-pointer text-sm text-brand-700">View {typeLabel} visit summary · {prettyDate(encounter.started_at)}</summary><div className="mt-3">{content}</div></details>;
}

function StatusPill({ status }: { status: string }) {
    const cls =
        status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
        status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
        status === 'cancelled' ? 'bg-slate-200 text-slate-600' :
        'bg-slate-100 text-slate-600';
    const label =
        status === 'completed' ? 'Completed' :
        status === 'in_progress' ? 'In progress' :
        status === 'cancelled' ? 'Cancelled' :
        status === 'planned' ? 'Planned' : status;
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
            {label}
        </span>
    );
}

function prettyDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
